<div class="dash-shell">
  <header class="page-head">
    <nav class="page-head__crumbs" aria-label="Breadcrumb">
      <a class="page-head__crumb" routerLink="/agent/dashboard">Portfolio</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      <span class="page-head__crumb page-head__crumb--current" aria-current="page"
        >Add a client</span
      >
    </nav>
    <div class="page-head__bar">
      <div class="page-head__titles">
        <h1 class="page-head__title">Add a client business</h1>
        <p class="page-head__subtitle">
          Set up a business you represent and invite its owner. You can start an application
          straight away.
        </p>
      </div>
    </div>
  </header>

  <!-- Delegation boundary: make the "on behalf" model explicit up front -->
  <div class="onboard-note" role="note">
    <span class="onboard-note__icon"><app-icon name="shield" [size]="18" /></span>
    <p class="onboard-note__text">
      You'll act as this business's agent — preparing and submitting applications on their behalf.
      The owner sets up their own account and can see everything done on the business's behalf.
    </p>
  </div>

  <div class="onboard-card">
    @if (serverError()) {
      <div class="inline-error" role="alert" aria-live="assertive">
        {{ serverError() }}
      </div>
    }

    <form class="entity-form" [formGroup]="form" novalidate (ngSubmit)="onSubmit()">
      <!-- ─── The business ─────────────────────────────────────────────── -->
      <section class="form-section">
        <p class="form-section__label">The business</p>

        <div class="field-group">
          <label class="field-label" for="entityName">
            Business name <span class="required-mark" aria-hidden="true">*</span>
          </label>
          <mat-form-field appearance="outline" class="full-width">
            <input
              matInput
              id="entityName"
              type="text"
              formControlName="name"
              placeholder="e.g. Acme Pty Ltd"
              autocomplete="organization"
            />
            <mat-error>
              @if (form.controls.name.hasError('required')) {
                Business name is required
              } @else if (form.controls.name.hasError('maxlength')) {
                Name must be 255 characters or fewer
              } @else if (form.controls.name.hasError('serverError')) {
                {{ form.controls.name.getError('serverError') }}
              }
            </mat-error>
          </mat-form-field>
        </div>

        <div class="field-group">
          <label class="field-label" for="entityTradingName">Trading name</label>
          <mat-form-field appearance="outline" class="full-width">
            <input
              matInput
              id="entityTradingName"
              type="text"
              formControlName="tradingName"
              placeholder="Defaults to business name"
            />
            <mat-hint>Defaults to business name; update if different</mat-hint>
            <mat-error>
              @if (form.controls.tradingName.hasError('maxlength')) {
                Trading name must be 255 characters or fewer
              } @else if (form.controls.tradingName.hasError('serverError')) {
                {{ form.controls.tradingName.getError('serverError') }}
              }
            </mat-error>
          </mat-form-field>
        </div>

        <div class="field-group">
          <label class="field-label" for="entityType" id="entityTypeLabel">
            Business type <span class="required-mark" aria-hidden="true">*</span>
          </label>
          <mat-form-field appearance="outline" class="full-width">
            <mat-select
              id="entityType"
              formControlName="type"
              placeholder="Select a business type"
              aria-labelledby="entityTypeLabel"
            >
              @for (opt of ENTITY_TYPES; track opt.value) {
                <mat-option [value]="opt.value">{{ opt.label }}</mat-option>
              }
            </mat-select>
            @if (typeHint(); as hint) {
              <mat-hint data-testid="entity-type-hint">
                The registration number usually means {{ TYPE_LABELS[hint] }} — check the CIPC
                record.
              </mat-hint>
            }
            <mat-error>
              @if (form.controls.type.hasError('required')) {
                Please select a business type
              } @else if (form.controls.type.hasError('serverError')) {
                {{ form.controls.type.getError('serverError') }}
              }
            </mat-error>
          </mat-form-field>
        </div>

        <div class="field-group">
          <label class="field-label" for="entityRegNo">
            Registration number <span class="required-mark" aria-hidden="true">*</span>
          </label>
          <mat-form-field appearance="outline" class="full-width">
            <input
              matInput
              id="entityRegNo"
              type="text"
              formControlName="regNo"
              placeholder="e.g. 2023/000001/07"
              (blur)="onRegNoBlur()"
            />
            @if (regNoStatus() === 'checking') {
              <span matSuffix class="reg-no-spinner">
                <mat-spinner diameter="16" />
              </span>
            }
            <mat-error>
              @if (form.controls.regNo.hasError('required')) {
                Registration number is required
              } @else if (form.controls.regNo.hasError('regNoFormat')) {
                Use only numbers and forward slashes (/)
              } @else if (form.controls.regNo.hasError('taken')) {
                This business is already registered on CapX. If you believe this is in error, please
                contact
                <a href="mailto:support@flow.capx.co.za">support&#64;flow.capx.co.za</a>.
              } @else if (form.controls.regNo.hasError('serverError')) {
                {{ form.controls.regNo.getError('serverError') }}
              }
            </mat-error>
          </mat-form-field>
          @if (regNoStatus() === 'available') {
            <p class="field-available" role="status" aria-live="polite">
              Registration number is available
            </p>
          }
          @if (nameToConfirm(); as name) {
            <app-company-name-check [name]="name" (answered)="answerNameCheck($event)" />
          }
        </div>

        <div class="field-group">
          <label class="field-label" for="entityIndustry">
            Industry <span class="required-mark" aria-hidden="true">*</span>
          </label>
          <mat-form-field appearance="outline" class="full-width">
            <input
              matInput
              id="entityIndustry"
              type="text"
              formControlName="industry"
              placeholder="e.g. Retail, Construction, Technology"
            />
            <mat-error>
              @if (form.controls.industry.hasError('required')) {
                Industry is required
              } @else if (form.controls.industry.hasError('maxlength')) {
                Industry must be 255 characters or fewer
              } @else if (form.controls.industry.hasError('serverError')) {
                {{ form.controls.industry.getError('serverError') }}
              }
            </mat-error>
          </mat-form-field>
        </div>

        <div class="field-group">
          <p class="field-label" id="tradingStartLabel">
            When did the business start trading?
            <span class="required-mark" aria-hidden="true">*</span>
          </p>
          <p class="form-section__hint" id="tradingStartHint">
            The business's own account. CapX takes the company's registration date from its CIPC
            records, not from this.
          </p>
          <div class="trading-start-row">
            <mat-form-field appearance="outline" class="trading-start-row__month">
              <mat-select
                formControlName="tradingStartMonth"
                placeholder="Month"
                aria-labelledby="tradingStartLabel"
                aria-describedby="tradingStartHint"
              >
                @for (m of MONTHS; track m.value) {
                  <mat-option [value]="m.value">{{ m.label }}</mat-option>
                }
              </mat-select>
              <mat-error>
                @if (form.controls.tradingStartMonth.hasError('required')) {
                  Required
                } @else if (form.controls.tradingStartMonth.hasError('tradingStartInFuture')) {
                  Trading start date cannot be in the future
                } @else if (form.controls.tradingStartMonth.hasError('serverError')) {
                  {{ form.controls.tradingStartMonth.getError('serverError') }}
                }
              </mat-error>
            </mat-form-field>

            <mat-form-field appearance="outline" class="trading-start-row__year">
              <input
                matInput
                id="tradingStartYear"
                type="number"
                formControlName="tradingStartYear"
                placeholder="Year"
                min="1900"
                aria-labelledby="tradingStartLabel"
                aria-describedby="tradingStartHint"
              />
              <mat-error>
                @if (form.controls.tradingStartYear.hasError('required')) {
                  Required
                } @else if (form.controls.tradingStartYear.hasError('min')) {
                  Year must be 1900 or later
                } @else if (form.controls.tradingStartYear.hasError('max')) {
                  Year cannot be in the future
                } @else if (form.controls.tradingStartYear.hasError('serverError')) {
                  {{ form.controls.tradingStartYear.getError('serverError') }}
                }
              </mat-error>
            </mat-form-field>
          </div>
        </div>
      </section>

      <!-- ─── The responsible person ───────────────────────────────────── -->
      <section class="form-section">
        <p class="form-section__label">Responsible person</p>
        <p class="form-section__hint">
          The business owner or director you're acting for. They'll be invited to set a password and
          take ownership of the account.
        </p>

        <div class="field-row">
          <div class="field-group">
            <label class="field-label" for="clientFirstName">
              First name <span class="required-mark" aria-hidden="true">*</span>
            </label>
            <mat-form-field appearance="outline" class="full-width">
              <input
                matInput
                id="clientFirstName"
                type="text"
                formControlName="clientFirstName"
                placeholder="e.g. Thandi"
                autocomplete="off"
              />
              <mat-error>
                @if (form.controls.clientFirstName.hasError('required')) {
                  First name is required
                } @else if (form.controls.clientFirstName.hasError('serverError')) {
                  {{ form.controls.clientFirstName.getError('serverError') }}
                }
              </mat-error>
            </mat-form-field>
          </div>

          <div class="field-group">
            <label class="field-label" for="clientLastName">
              Last name <span class="required-mark" aria-hidden="true">*</span>
            </label>
            <mat-form-field appearance="outline" class="full-width">
              <input
                matInput
                id="clientLastName"
                type="text"
                formControlName="clientLastName"
                placeholder="e.g. Nkosi"
                autocomplete="off"
              />
              <mat-error>
                @if (form.controls.clientLastName.hasError('required')) {
                  Last name is required
                } @else if (form.controls.clientLastName.hasError('serverError')) {
                  {{ form.controls.clientLastName.getError('serverError') }}
                }
              </mat-error>
            </mat-form-field>
          </div>
        </div>

        <div class="field-group">
          <label class="field-label" for="clientEmail">
            Their email <span class="required-mark" aria-hidden="true">*</span>
          </label>
          <mat-form-field appearance="outline" class="full-width">
            <input
              matInput
              id="clientEmail"
              type="email"
              formControlName="clientEmail"
              placeholder="owner@business.co.za"
              autocomplete="off"
            />
            <mat-hint
              >We'll send the invitation here. Use your client's address, not yours.</mat-hint
            >
            <mat-error>
              @if (form.controls.clientEmail.hasError('required')) {
                Their email is required
              } @else if (form.controls.clientEmail.hasError('email')) {
                Enter a valid email address
              } @else if (form.controls.clientEmail.hasError('serverError')) {
                {{ form.controls.clientEmail.getError('serverError') }}
              }
            </mat-error>
          </mat-form-field>
        </div>
      </section>

      <!-- Preparer attestation — consent_text_version: 2026-11-01. Legal's words, final (DOC-75 §2.4) -->
      <section class="consent-section">
        <h2 class="consent-section__heading">Authority and consent</h2>
        <mat-checkbox formControlName="consent" (change)="onConsentChange($event.checked)">
          I confirm that I am authorised to act for this business. On its behalf, I consent to CapX
          processing the business's information and carrying out credit and compliance checks on it,
          including with credit bureaux, to assess this application and, if it is funded, to monitor
          it. A credit check on any individual — a director, or anyone giving security for the
          business — needs that person's own consent, which CapX will ask them for separately.
        </mat-checkbox>
        <p class="consent-section__text">
          CapX uses the information collected here to assess this application, to manage any funding
          that follows, and to meet its legal obligations, in line with the Protection of Personal
          Information Act. CapX will carry out a credit check on an individual only once that person
          has given their own consent.
        </p>
        @if (form.controls.consent.touched && form.controls.consent.hasError('requiredTrue')) {
          <p class="consent-error" role="alert">Tick the box to give this consent and continue.</p>
        }
      </section>

      <div class="form-actions">
        <a class="btn-ghost" routerLink="/agent/dashboard">Cancel</a>
        <button
          mat-flat-button
          type="submit"
          data-testid="onboard-client-submit"
          [disabled]="isSubmitDisabled"
          [attr.aria-busy]="isLoading() || null"
        >
          @if (isLoading()) {
            <mat-spinner diameter="20" aria-hidden="true" />
            <span>Adding client…</span>
          } @else {
            Add client &amp; send invite
          }
        </button>
      </div>
    </form>
  </div>
</div>
